// Deterministic derivation primitives for building pattern manifests
// from entity data. Two helpers cover every consumer derivation case:
//
//   • `hashStringToSeed(id)` — turn any stable string ID into a stable
//     32-bit integer suitable for a manifest's `seed` field. Same ID
//     always produces the same seed forever, so the rendered pattern
//     is reproducible per entity.
//
//   • `pickFromHash(items, key)` — deterministically choose one entry
//     from a list by hashing the key. Useful when an entity needs a
//     stable variant / palette / etc. without any user input.
//
// CANONICAL DERIVATION RECIPE (Morfi convention)
// ──────────────────────────────────────────────
// Consumers DO NOT take user input for palette / seed / params and DO NOT
// persist manifests. Every manifest is derived at render time from the
// entity's existing fields:
//
//   const manifest: PatternManifest = {
//     version: 1,
//     source: { kind: 'url', url: market.coverImageUrl },
//     // Per-market identity color — derived from the title via hash mode (no
//     // positional index in scope here). Spec 064 made precise-order the
//     // default, so callers without an `index` MUST opt into 'hash' explicitly.
//     palette: { kind: 'outcome-hue', hue: outcomeHueFromTitle(market.title, undefined, 'hash') },
//     seed: hashStringToSeed(market.id),
//   };
//
// For synthetic placeholder sources the variant is baked into the source
// descriptor; everything else derives from the entity ID:
//
//   const manifest: PatternManifest = {
//     version: 1,
//     source: {
//       kind: 'synthetic',
//       variant: profile.preferredVariant ?? pickFromHash(VARIANTS, profile.id),
//       seed: hashStringToSeed(profile.id),
//     },
//     palette: { kind: 'preset', name: 'brand' },
//     seed: hashStringToSeed(profile.id),
//   };

/**
 * FNV-1a string hash → stable positive 32-bit integer suitable for the
 * `seed` field of a `PatternManifest`. Same input always produces the
 * same output forever; collision rate is fine for visual identity (~1
 * in 4 billion).
 */
export function hashStringToSeed(str: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * Deterministically pick one entry from a list by hashing a string key.
 * Returns the first entry if `items` is empty (defensive — callers
 * should ensure `items` has at least one element).
 *
 * Same key + same items array always returns the same entry. Useful for
 * giving each entity a stable, varied generative variant / palette
 * choice / etc. without any persistence.
 */
export function pickFromHash<T>(items: readonly T[], key: string): T {
  const idx = hashStringToSeed(key) % items.length;
  const picked = items[idx];
  if (picked === undefined) {
    const fallback = items[0];
    if (fallback === undefined) {
      throw new Error('pickFromHash: items array is empty');
    }
    return fallback;
  }
  return picked;
}
